import moment from 'moment-timezone';
import { parseJsonObject } from 'src/utils/json';

/**
 * Why a donation detail request didn't produce a donation.
 *
 * The detail endpoint names the reason in the `code` of its 403/404 body
 * (claimed by someone else, removed, the pickup was cancelled, ...), so the
 * screen can say what happened and offer the next step. Older backends, and
 * failures that never reached the server, carry no code; those keep the
 * generic copy. Above all, a failed request must not be reported as "this
 * donation no longer exists", which is the bug this classification exists to
 * prevent.
 */
export const DONATION_ERROR = {
  /** The request failed without establishing availability. Offers a retry. */
  LOAD_FAILED: 'load-failed',
  /** Server answered, but the donation isn't there for this user. */
  UNAVAILABLE: 'unavailable',
  /**
   * The request succeeded but matched no donation, e.g. a rescueId that no
   * longer points at an active rescue. The reason can't be established.
   */
  NOT_FOUND: 'not-found',
};

/** Recovery actions offered for an unavailable donation. */
export const DONATION_ACTION = {
  RETRY: 'retry',
  FIND_DONATIONS: 'find-donations',
  SWITCH_ORGANIZATION: 'switch-organization',
  VIEW_RESCUE: 'view-rescue',
  VIEW_RESCUE_RECORD: 'view-rescue-record',
  VIEW_ASSIGNMENTS: 'view-assignments',
};

/**
 * Reasons the backend gives for a donation the caller can't act on: the `code`
 * of a 403/404 from `GET /donation/:id`, or `availability.status` on a 200.
 * Message keys live under `donations.unavailable.*`; `partialMessageKey`
 * replaces `messageKey` for partial donations.
 */
export const DONATION_UNAVAILABLE_REASONS = {
  CLAIMED_BY_OTHER: {
    messageKey: 'claimed',
    partialMessageKey: 'claimed-partial',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  REMOVED: {
    messageKey: 'removed',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  PICKUP_WINDOW_ENDED: {
    messageKey: 'pickup-window-ended',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  ALREADY_COLLECTED: {
    messageKey: 'already-collected',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  ASSIGNED_TO_SPECIFIC_RECIPIENT: {
    messageKey: 'assigned-to-specific-recipient',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  NOT_OPEN_TO_YOUR_ORG: {
    messageKey: 'not-open-to-your-org',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  // No page a recipient can reach shows why their pickup was cancelled: the
  // donation page (and its activity log) is what's blocked, the activity log
  // doesn't render donor-side cancellations, and the cancellation report only
  // lists the viewer's own team's cancellations. Finding other food is the
  // useful next step.
  PICKUP_CANCELLED: {
    messageKey: 'pickup-cancelled',
    action: DONATION_ACTION.FIND_DONATIONS,
  },
  RESERVED_BY_YOUR_ORG: {
    messageKey: 'reserved-by-your-org',
    action: DONATION_ACTION.VIEW_RESCUE,
  },
  COMPLETED_BY_YOUR_ORG: {
    messageKey: 'completed-by-your-org',
    action: DONATION_ACTION.VIEW_RESCUE_RECORD,
  },
  REASSIGNED_TO_OTHER_DRIVER: {
    messageKey: 'reassigned-to-other-driver',
    action: DONATION_ACTION.VIEW_ASSIGNMENTS,
  },
  DRIVER_CANNOT_VIEW_OPEN: {
    messageKey: 'driver-cannot-view-open',
    action: DONATION_ACTION.VIEW_ASSIGNMENTS,
  },
  WRONG_ORGANIZATION: {
    messageKey: 'wrong-organization',
    action: DONATION_ACTION.SWITCH_ORGANIZATION,
  },
};

// 404 for a rescueId that no longer matches an active rescue. Says nothing
// more specific than "nothing to show here".
const RESCUE_NOT_FOUND = 'RESCUE_NOT_FOUND';

const getErrorCode = (error) => error?.code ?? error?.data?.code ?? null;

const isKnownReason = (code) =>
  typeof code === 'string' &&
  Object.prototype.hasOwnProperty.call(DONATION_UNAVAILABLE_REASONS, code);

/**
 * @typedef {object} DonationUnavailableReason
 * @property {string} code one of DONATION_UNAVAILABLE_REASONS
 * @property {string} messageKey full i18n key of the sentence to show
 * @property {string} action one of DONATION_ACTION
 * @property {boolean} isPartial
 * @property {{ start_date: string, end_date?: string, timezone_id?: string } | null} occurrence
 *   the recurring occurrence it's about, when there is one
 * @property {string | null} rescueId
 * @property {string[]} switchableTeamIds the user's other teams that can see it
 */

/** @returns {DonationUnavailableReason} */
function describeReason(code, details = {}) {
  const reason = DONATION_UNAVAILABLE_REASONS[code];
  const isPartial = Boolean(details.is_partial);
  const occurrence = parseJsonObject(details.occurrence, null);
  return {
    code,
    messageKey: `donations.unavailable.${
      isPartial && reason.partialMessageKey
        ? reason.partialMessageKey
        : reason.messageKey
    }`,
    action: reason.action,
    isPartial,
    occurrence: occurrence?.start_date ? occurrence : null,
    rescueId: details.rescue_id ?? null,
    switchableTeamIds: Array.isArray(details.switchable_team_ids)
      ? details.switchable_team_ids.map(String)
      : [],
  };
}

/**
 * Classify a donation fetch failure.
 * @param {Error & {status?: number, code?: string, data?: object}} error
 * @returns {string | null} a DONATION_ERROR value, or null when there's no error
 */
export function classifyDonationError(error) {
  if (!error) return null;

  // A named reason is the server telling us what happened to the donation.
  const code = getErrorCode(error);
  if (code === RESCUE_NOT_FOUND) return DONATION_ERROR.NOT_FOUND;
  if (isKnownReason(code)) return DONATION_ERROR.UNAVAILABLE;

  // fetch() rejects without a status when the request never completed
  // (offline, DNS, timeout, CORS). Nothing was learned about the donation.
  if (typeof error.status !== 'number') return DONATION_ERROR.LOAD_FAILED;

  // The server failed to answer the question; that says nothing about the food.
  if (error.status >= 500) return DONATION_ERROR.LOAD_FAILED;

  // A 403 without a reason we know (older backends) still means unavailable
  // to you, just without saying why.
  if (error.status === 403) return DONATION_ERROR.UNAVAILABLE;

  // Other failures, including expired sessions and rate limits, do not tell us
  // whether the donation is available. The detail endpoint only answers 404
  // with a code, handled above.
  return DONATION_ERROR.LOAD_FAILED;
}

/**
 * The reason the server named for a donation fetch failure, or null when it
 * named none this client knows (old backend, no body, unknown code), in which
 * case the generic copy applies.
 * @param {Error & {code?: string, data?: object}} error
 * @returns {DonationUnavailableReason | null}
 */
export function getDonationUnavailableReason(error) {
  const code = getErrorCode(error);
  if (!isKnownReason(code)) return null;
  return describeReason(code, error.data ?? {});
}

/**
 * The user's other organizations worth offering a switch to: only those the
 * server says can see the donation. Without that list (any other reason, or an
 * older backend) switching is a guess that may fail the same way, so none are
 * offered.
 * @param {Array<{ team_id: string }>} teams the user's organizations
 * @param {DonationUnavailableReason | null} reason
 * @param {string} [currentTeamId]
 */
export function getSwitchableOrganizations(teams, reason, currentTeamId) {
  const switchable = reason?.switchableTeamIds ?? [];
  if (!switchable.length) return [];
  return (teams ?? []).filter(
    (team) =>
      String(team.team_id) !== String(currentTeamId) &&
      switchable.includes(String(team.team_id))
  );
}

/**
 * True when the user can reasonably retry the same request.
 * @param {string | null} kind
 */
export function isRetryable(kind) {
  return kind === DONATION_ERROR.LOAD_FAILED;
}

/**
 * True once a donation's pickup window has closed. Find Donations hides such a
 * donation with the same `end_date >= NOW()` rule. A fallback for when the
 * backend's `availability` doesn't settle it.
 * @param {{ end_date?: string }} donation
 * @param {moment.Moment} [now]
 */
export function isPickupWindowEnded(donation, now = moment()) {
  if (!donation?.end_date) return false;
  const end = moment.utc(donation.end_date);
  return end.isValid() && end.isBefore(now);
}

/**
 * Whether to withhold Claim because the pickup window has closed. The
 * backend's OPEN / PICKUP_WINDOW_ENDED status is authoritative; other statuses
 * (e.g. your own cancelled pickup) don't say, so the client clock decides, as
 * it does for backends that send no `availability`.
 * @param {object} donation the loaded donation row
 * @param {moment.Moment} [now]
 */
export function isClaimWindowClosed(donation, now = moment()) {
  const status = parseJsonObject(donation?.availability, null)?.status;
  if (status === 'PICKUP_WINDOW_ENDED') return true;
  if (status === 'OPEN') return false;
  return isPickupWindowEnded(donation, now);
}

/**
 * The notice to show on a donation that loaded but isn't claimable as usual,
 * from the backend's per-row `availability`. Older backends send none, so a
 * claimable donation whose window has passed falls back to the client clock.
 *
 * @param {object} args
 * @param {object} args.donation the loaded donation row
 * @param {string} [args.rescueId] the rescue named in the URL, if any
 * @param {boolean} [args.claimable] whether the viewer could otherwise claim it
 * @param {{ isDonorTeam?: boolean, isFoodMoverTeam?: boolean, isReadOnly?: boolean }} [args.viewer]
 * @param {moment.Moment} [args.now]
 * @returns {DonationUnavailableReason | null}
 */
export function getDonationAvailabilityNotice({
  donation,
  rescueId,
  claimable = false,
  viewer = {},
  now = moment(),
}) {
  if (!donation) return null;
  // Notices are for the organization that would pick the food up. Donors and
  // food movers manage the donation, and read-only viewers can't act on it.
  if (viewer.isDonorTeam || viewer.isFoodMoverTeam || viewer.isReadOnly) {
    return null;
  }

  const availability = parseJsonObject(donation.availability, null);
  const details = { is_partial: donation.is_partial, ...availability };

  if (!availability) {
    return claimable && isPickupWindowEnded(donation, now)
      ? describeReason('PICKUP_WINDOW_ENDED', details)
      : null;
  }

  switch (availability.status) {
    case 'PICKUP_WINDOW_ENDED':
    case 'PICKUP_CANCELLED':
      return describeReason(availability.status, details);
    case 'RESERVED_BY_YOUR_ORG':
    case 'COMPLETED_BY_YOUR_ORG': {
      // Only worth saying when this page isn't already showing that rescue,
      // e.g. a partial donation's overview opened without a rescue id.
      const shownRescueId = rescueId || donation.rescue_id;
      if (
        !availability.rescue_id ||
        String(availability.rescue_id) === String(shownRescueId)
      ) {
        return null;
      }
      return describeReason(availability.status, details);
    }
    default:
      return null;
  }
}

/**
 * The day a recurring occurrence is for, in the donor's timezone, e.g.
 * "Fri, Oct 2". Dates arrive in UTC.
 * @param {{ start_date?: string, timezone_id?: string } | null} occurrence
 * @returns {string | null}
 */
export function formatOccurrenceDate(occurrence) {
  if (!occurrence?.start_date) return null;
  const start = moment.utc(occurrence.start_date);
  if (!start.isValid()) return null;
  const timezoneId = occurrence.timezone_id;
  return (
    timezoneId && moment.tz.zone(timezoneId)
      ? start.tz(timezoneId)
      : start.local()
  ).format('ddd, MMM D');
}
